import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { Textarea } from "@gigpro/ui/Form/Textarea";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { toast } from "@gigpro/ui/Toast";
import { formatPhoneNumber } from "@gigpro/utils";
import {
  type AiInterviewInviteResponse,
  getAdminGigsterProfilesAiInterviewInvitesRetrieveQueryKey,
  useAdminGigsterProfilesAiInterviewInvitesCreate,
  useAdminGigsterProfilesAiInterviewInvitesRetrieve,
} from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { type SubmitHandler, useWatch } from "react-hook-form";
import { toastApiError } from "utils/toastApiError";
import * as z from "zod/mini";
import { AiInterviewSmsPreview } from "./AiInterviewSmsPreview";
import { AiInterviewStateChip } from "./AiInterviewStateChip";
import { APPENDED_LINK_LENGTH, DEFAULT_INVITE_MESSAGE, MESSAGE_MAX_LENGTH } from "./constants";

const formSchema = z.object({
  message: z
    .string()
    .check(z.trim(), z.maxLength(MESSAGE_MAX_LENGTH, `Keep the message under ${MESSAGE_MAX_LENGTH} characters.`)),
});
type FormValues = z.infer<typeof formSchema>;

const relative = (date: Date | null) => (date ? formatDistanceToNow(date, { addSuffix: true }) : "");

/**
 * States meaning an invite is already in flight, so a fresh send supersedes it and the
 * admin deserves the same soft warning `PENDING` gets. A Map because the key is an
 * untrusted API string — `{}[state]` would hit an inherited method for e.g. "toString".
 */
const IN_FLIGHT_NOTE = new Map<string, string>([
  ["PAUSED", "This pro started an interview and paused it"],
  ["EXHAUSTED", "This pro has used up their attempts on the current invite"],
  ["ERROR", "This pro's last interview failed to process"],
]);

function softWarnFor(invite: AiInterviewInviteResponse | undefined): string | null {
  if (!invite) return null;
  const pending = invite.current_state === "PENDING";
  const completed = invite.completed_at != null;

  if (pending && completed) {
    return `An invite is still pending (sent ${relative(invite.sent_at)}), and this pro completed an interview ${relative(invite.completed_at)}. Send another?`;
  }
  if (pending) {
    return `An invite is still pending (sent ${relative(invite.sent_at)}). Send another? The previous link keeps working until it expires; this supersedes it.`;
  }

  const inFlightNote = IN_FLIGHT_NOTE.get(invite.current_state);
  if (inFlightNote) {
    const sent = relative(invite.sent_at);
    const priorCompletion = completed ? ` A prior interview was completed ${relative(invite.completed_at)}.` : "";
    const sentPhrase = sent ? ` (link sent ${sent})` : "";
    return `${inFlightNote}${sentPhrase}. Send another? This supersedes the existing invite.${priorCompletion}`;
  }

  if (invite.current_state === "COMPLETED") {
    return `This pro completed an interview ${relative(invite.completed_at)}. Send another anyway?`;
  }
  return null;
}

type Props = {
  proId: number;
  phone: string | null;
  open: boolean;
  onClose: () => void;
};

/**
 * Confirm dialog for texting (and pushing) the AI onboarding interview link. Reads
 * the pro's current state on open (read-before-write) and soft-warns — never blocks —
 * before sending over a pending/completed invite.
 */
export function ModalSendAiInterviewLink({ proId, phone, open, onClose }: Readonly<Props>) {
  // Body lives in a child so Radix unmounts it on close → the form + read reset for free.
  return (
    <Dialog open={open} onClose={onClose}>
      <SendInterviewLinkForm proId={proId} phone={phone} onClose={onClose} />
    </Dialog>
  );
}

function SendInterviewLinkForm({ proId, phone, onClose }: Readonly<Omit<Props, "open">>) {
  const queryClient = useQueryClient();
  const invite = useAdminGigsterProfilesAiInterviewInvitesRetrieve(proId);
  const sendInvite = useAdminGigsterProfilesAiInterviewInvitesCreate({
    mutation: {
      onSuccess: (data) =>
        queryClient.setQueryData(getAdminGigsterProfilesAiInterviewInvitesRetrieveQueryKey(proId), data),
    },
  });
  const form = useZodForm({ schema: formSchema, defaultValues: { message: DEFAULT_INVITE_MESSAGE } });

  const message = useWatch({ control: form.control, name: "message" }) ?? "";
  const warning = softWarnFor(invite.data);
  const phoneLabel = phone ? formatPhoneNumber(phone, { hasNoPrefix: true }) : null;

  const handleSubmit: SubmitHandler<FormValues> = async ({ message: value }) => {
    try {
      // Omit an empty message so the backend falls back to its canonical default.
      await sendInvite.mutateAsync({ userId: proId, data: value ? { message: value } : undefined });
      toast.success(phoneLabel ? `Interview link sent to ${phoneLabel}` : "Interview link sent");
      onClose();
    } catch (error) {
      toastApiError(error);
    }
  };

  return (
    <>
      <DialogTitle>Send interview link</DialogTitle>
      <p className="text-small text-gray">
        {phoneLabel
          ? `Sends the AI onboarding interview to ${phoneLabel} by text and push.`
          : "Sends the AI onboarding interview by text and push."}
      </p>

      <div className="flex items-center gap-2 text-small text-medium-gray">
        <span>Current status</span>
        {invite.isLoading ? (
          <span className="text-gray">checking…</span>
        ) : (
          <AiInterviewStateChip state={invite.data?.current_state ?? "NONE"} />
        )}
      </div>

      {warning && (
        <div role="note" className="border-l-2 border-medium-gray bg-background px-3 py-2 text-small text-gray">
          {warning}
        </div>
      )}

      <Form form={form} onSubmit={handleSubmit}>
        <Textarea name="message" control={form.control} label="Message (editable)" rows={3} />
        <p className="mt-1 text-extraSmall text-medium-gray">
          {message.length} chars + link ≈ {message.length + APPENDED_LINK_LENGTH}
        </p>
        <AiInterviewSmsPreview message={message} />
        <DialogFooter>
          <Button size="small" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            type="submit"
            loading={form.formState.isSubmitting}
            // Read-before-write: block send until the current-state read has settled
            // (covers a stale-data refetch too), so the soft-warn is never skipped.
            disabled={invite.isLoading || invite.isFetching}
          >
            Send invite
          </Button>
        </DialogFooter>
      </Form>
    </>
  );
}
